import { useEffect, useState } from 'react';
import { Card, DataTable, FilterBar, Select, StatusBadge, Drawer } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Icon } from '@/components/ui/Icon';
import { AudioPlayer } from '@/components/ui/AudioPlayer';
import { PhoneCell } from '@/components/telephony/PhoneCell';
import { agentApi, type CallRow, type CallTranscript } from '@/shared/api/agentApi';
import { labelFor, toneFor } from '@/shared/lib/labels';
import { toast } from '@/components/ui/Toast';

export function History() {
  const [direction, setDirection] = useState('');
  const [rows, setRows] = useState<CallRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [player, setPlayer] = useState<{ call: CallRow; url: string | null; loading: boolean } | null>(null);
  const [details, setDetails] = useState<CallRow | null>(null);
  const [dispoLabels, setDispoLabels] = useState<Record<string, string>>({});
  // Transcript + AI summary for the call open in the details drawer.
  const [tr, setTr] = useState<CallTranscript | null>(null);
  const [trLoading, setTrLoading] = useState(false);

  useEffect(() => {
    if (!details) { setTr(null); return; }
    setTrLoading(true);
    let alive = true;
    void agentApi.callTranscript(details.id)
      .then((t) => { if (alive) setTr(t); })
      .catch(() => { if (alive) setTr(null); })
      .finally(() => { if (alive) setTrLoading(false); });
    return () => { alive = false; };
  }, [details]);

  useEffect(() => {
    void agentApi.callDispositions()
      .then((ds) => setDispoLabels(Object.fromEntries((ds as { code: string; label: string }[]).map((d) => [d.code, d.label]))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    void agentApi
      .calls(direction || undefined)
      .then((res) => setRows(res.data))
      .finally(() => setLoading(false));
  }, [direction]);

  async function openRecording(call: CallRow) {
    setPlayer({ call, url: null, loading: true });
    try {
      const rec = await agentApi.callRecording(call.id);
      setPlayer({ call, url: rec?.url ?? null, loading: false });
    } catch {
      setPlayer({ call, url: null, loading: false });
      toast('שגיאה בטעינת ההקלטה', 'danger');
    }
  }

  const filtered = q
    ? rows.filter((r) => r.from?.includes(q) || r.to?.includes(q))
    : rows;

  const cols: DataTableColumn<CallRow>[] = [
    {
      key: 'direction',
      he: 'כיוון',
      render: (r) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Icon
            name={r.direction === 'inbound' ? 'phoneIn' : r.direction === 'outbound' ? 'phoneOut' : 'phone'}
            size={14}
            color={r.direction === 'inbound' ? 'var(--ok)' : r.direction === 'outbound' ? 'var(--info)' : 'var(--muted)'}
          />
          {r.direction === 'inbound' ? 'נכנסת' : r.direction === 'outbound' ? 'יוצאת' : 'פנימית'}
        </span>
      ),
    },
    {
      key: 'other',
      he: 'הצד השני',
      render: (r) => (
        <button
          type="button"
          onClick={() => setDetails(r)}
          title="הצג תיעוד שיחה"
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'start' }}
        >
          <PhoneCell
            number={r.direction === 'inbound' ? r.from : r.to}
            lookup={r.other_party as never}
            size="sm"
          />
        </button>
      ),
    },
    { key: 'started_at', he: 'מתי', render: (r) => formatDateTime(r.started_at) },
    { key: 'duration_sec', he: 'משך', render: (r) => fmtDur(r.duration_sec) },
    {
      key: 'status',
      he: 'סטטוס',
      render: (r) => (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <StatusBadge tone={toneFor(r.status)} size="sm">{labelFor('call_status', r.status)}</StatusBadge>
          {/* אפיון 5.12: שיחה שנסגרה בלי תיעוד מסומנת ככזו, כדי שהנציג
              יראה מה נשאר פתוח אחריו ולא רק את תוצאת השיחה. */}
          {r.undocumented && (
            <StatusBadge tone="warn" size="sm">לא תועד</StatusBadge>
          )}
        </div>
      ),
    },
    {
      key: '_rec',
      he: 'הקלטה',
      render: (r) =>
        r.has_recording ? (
          <button
            type="button"
            className="btn ghost"
            onClick={(e) => { e.stopPropagation(); void openRecording(r); }}
            aria-label="האזן להקלטה"
            title="האזן להקלטה"
            style={{ height: 30, padding: '0 10px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Icon name="play" size={14} color="var(--accent)" /> האזן
          </button>
        ) : (
          <span style={{ color: 'var(--faint)', fontSize: 'var(--t-xs)' }}>—</span>
        ),
    },
  ];

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <FilterBar
        search={q}
        onSearch={setQ}
        onClear={() => setQ('')}
        placeholder="חיפוש לפי מספר…"
      >
        <Select
          value={direction}
          onChange={setDirection}
          options={[
            { value: '', label: 'הכל' },
            { value: 'inbound', label: 'נכנסות' },
            { value: 'outbound', label: 'יוצאות' },
          ]}
        />
      </FilterBar>
      <Card padded={false}>
        <DataTable<CallRow> columns={cols} rows={filtered} loading={loading} />
      </Card>

      <Drawer
        isOpen={!!player}
        onClose={() => setPlayer(null)}
        title="הקלטת שיחה"
        width={420}
      >
        {player && (
          <div className="stack">
            <div style={{ fontSize: 'var(--t-sm)', color: 'var(--text-2)' }}>
              <PhoneCell
                number={player.call.direction === 'inbound' ? player.call.from : player.call.to}
                lookup={player.call.other_party as never}
                size="sm"
              />
              <div style={{ marginTop: 4 }}>{formatDateTime(player.call.started_at)} · {fmtDur(player.call.duration_sec)}</div>
            </div>
            {player.loading ? (
              <div style={{ color: 'var(--muted)' }}>טוען הקלטה…</div>
            ) : player.url ? (
              <AudioPlayer src={player.url} duration={player.call.talk_sec || player.call.duration_sec} />
            ) : (
              <div style={{ color: 'var(--muted)' }}>ההקלטה אינה זמינה.</div>
            )}
          </div>
        )}
      </Drawer>

      {/* Call documentation (wrap-up): disposition, notes, tags */}
      <Drawer
        isOpen={!!details}
        onClose={() => setDetails(null)}
        title="תיעוד שיחה"
        width={420}
      >
        {details && (
          <div className="stack">
            <div style={{ fontSize: 'var(--t-sm)', color: 'var(--text-2)' }}>
              <PhoneCell
                number={details.direction === 'inbound' ? details.from : details.to}
                lookup={details.other_party as never}
                size="sm"
              />
              <div style={{ marginTop: 4 }}>{formatDateTime(details.started_at)} · {fmtDur(details.duration_sec)}</div>
            </div>

            {!details.disposition && !details.wrapup?.notes && !details.wrapup?.tags?.length ? (
              <Card><span style={{ color: 'var(--muted)' }}>השיחה לא תועדה.</span></Card>
            ) : (
              <Card>
                <div className="stack" style={{ gap: 12 }}>
                  {details.disposition && (
                    <div>
                      <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginBottom: 4 }}>קוד סיום</div>
                      <StatusBadge tone={toneFor(details.disposition)} size="sm">
                        {dispoLabels[details.disposition] ?? details.disposition}
                      </StatusBadge>
                    </div>
                  )}
                  {details.wrapup?.tags && details.wrapup.tags.length > 0 && (
                    <div>
                      <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginBottom: 4 }}>תגיות</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {details.wrapup.tags.map((t) => (
                          <span key={t} style={{ fontSize: 'var(--t-xs)', background: 'var(--surface-2)', borderRadius: 'var(--r-1)', padding: '2px 8px' }}>{t}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {details.wrapup?.notes && (
                    <div>
                      <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginBottom: 4 }}>הערות</div>
                      <div style={{ fontSize: 'var(--t-sm)', whiteSpace: 'pre-wrap' }}>{details.wrapup.notes}</div>
                    </div>
                  )}
                  {details.wrapup?.at && (
                    <div style={{ fontSize: 'var(--t-xs)', color: 'var(--faint)' }}>תועד: {formatDateTime(details.wrapup.at)}</div>
                  )}
                </div>
              </Card>
            )}

            {/* Auto transcript + AI summary of this past call */}
            <Card>
              <div className="stack" style={{ gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Icon name="ai" size={14} color="var(--accent)" />
                  <strong style={{ fontSize: 'var(--t-sm)' }}>סיכום ותמלול אוטומטי</strong>
                </div>
                {trLoading ? (
                  <span style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>טוען…</span>
                ) : tr && tr.summary ? (
                  <>
                    {/* Semantic badges: sentiment + trend + resolution + agent score */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                      {tr.sentiment && (
                        <StatusBadge tone={tr.sentiment === 'positive' ? 'ok' : tr.sentiment === 'negative' ? 'danger' : 'neutral'} size="sm">
                          {tr.sentiment === 'positive' ? 'חיובי' : tr.sentiment === 'negative' ? 'שלילי' : 'ניטרלי'}
                        </StatusBadge>
                      )}
                      {tr.metrics?.sentiment_trend && tr.metrics.sentiment_trend !== 'stable' && (
                        <StatusBadge tone={tr.metrics.sentiment_trend === 'improved' ? 'ok' : 'danger'} size="sm">
                          {tr.metrics.sentiment_trend === 'improved' ? '↗ השתפר במהלך השיחה' : '↘ הידרדר במהלך השיחה'}
                        </StatusBadge>
                      )}
                      {tr.metrics?.resolution && (
                        <StatusBadge tone={tr.metrics.resolution === 'resolved' ? 'ok' : tr.metrics.resolution === 'unresolved' ? 'danger' : 'warn'} size="sm">
                          {tr.metrics.resolution === 'resolved' ? 'הפנייה נסגרה' : tr.metrics.resolution === 'unresolved' ? 'לא נפתרה' : 'דורש המשך טיפול'}
                        </StatusBadge>
                      )}
                      {typeof tr.metrics?.score === 'number' && (
                        <span title="ציון איכות טיפול (AI)" style={{
                          fontSize: 'var(--t-xs)', fontWeight: 700, padding: '2px 8px', borderRadius: 999,
                          background: tr.metrics.score >= 75 ? 'var(--ok-soft, #E7F6EE)' : tr.metrics.score >= 50 ? 'var(--warn-soft, #FFF4E0)' : 'var(--danger-soft, #FDECEA)',
                          color: tr.metrics.score >= 75 ? 'var(--ok, #1F7A50)' : tr.metrics.score >= 50 ? '#9A6700' : 'var(--danger, #B3261E)',
                        }}>ציון {tr.metrics.score}</span>
                      )}
                    </div>

                    <div style={{ fontSize: 'var(--t-sm)', lineHeight: 1.6 }}>{tr.summary}</div>

                    {/* Topics */}
                    {tr.metrics?.topics && tr.metrics.topics.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {tr.metrics.topics.map((t, i) => (
                          <span key={i} style={{ fontSize: 'var(--t-xs)', padding: '2px 9px', borderRadius: 999, background: 'var(--surface-2, #F5F6F8)', border: '1px solid var(--line, #E6ECEF)', color: 'var(--text-2)' }}>{t}</span>
                        ))}
                      </div>
                    )}

                    {tr.highlights.length > 0 && (
                      <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: 'var(--t-sm)', color: 'var(--text-2)' }}>
                        {tr.highlights.map((h, i) => <li key={i}>{h}</li>)}
                      </ul>
                    )}

                    {/* Action items agreed on the call */}
                    {tr.metrics?.action_items && tr.metrics.action_items.length > 0 && (
                      <div className="stack" style={{ gap: 4 }}>
                        <strong style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>משימות המשך</strong>
                        {tr.metrics.action_items.map((a, i) => (
                          <div key={i} style={{ fontSize: 'var(--t-sm)' }}>☐ {a}</div>
                        ))}
                      </div>
                    )}

                    {/* Hard call metrics: talk ratio + pace */}
                    {(tr.metrics?.agent_talk_pct != null || tr.metrics?.wpm != null) && (
                      <div className="stack" style={{ gap: 5, borderTop: '1px solid var(--line, #E6ECEF)', paddingTop: 8 }}>
                        {tr.metrics?.agent_talk_pct != null && (
                          <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
                              <span>נציג {tr.metrics.agent_talk_pct}%</span>
                              <span>יחס דיבור</span>
                              <span>לקוח {tr.metrics.customer_talk_pct}%</span>
                            </div>
                            <div style={{ display: 'flex', height: 6, borderRadius: 999, overflow: 'hidden', background: 'var(--surface-2, #F5F6F8)' }}>
                              <div style={{ width: `${tr.metrics.agent_talk_pct}%`, background: 'var(--accent, #2DACDF)' }} />
                              <div style={{ flex: 1, background: 'var(--line, #E6ECEF)' }} />
                            </div>
                          </>
                        )}
                        <div style={{ display: 'flex', gap: 14, fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
                          {tr.duration_sec != null && tr.duration_sec > 0 && <span>משך: {Math.floor(tr.duration_sec / 60)}:{String(tr.duration_sec % 60).padStart(2, '0')}</span>}
                          {tr.metrics?.wpm != null && <span>קצב: {tr.metrics.wpm} מילים/דקה</span>}
                          {tr.metrics?.words_total != null && <span>{tr.metrics.words_total} מילים</span>}
                        </div>
                      </div>
                    )}
                  </>
                ) : tr && (tr.transcript_status === 'processing' || tr.transcript_status === 'pending' || tr.transcript_status === 'queued') ? (
                  <span style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>התמלול עדיין בעיבוד.</span>
                ) : (
                  <span style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>אין סיכום זמין לשיחה זו.</span>
                )}

                {tr && tr.transcript.length > 0 && (
                  <details>
                    <summary style={{ cursor: 'pointer', fontSize: 'var(--t-sm)', color: 'var(--accent-ink)' }}>הצג תמלול מלא</summary>
                    <div className="stack" style={{ gap: 4, marginTop: 8, maxHeight: 240, overflow: 'auto' }}>
                      {tr.transcript.map((line, i) => (
                        <div key={i} style={{ fontSize: 'var(--t-sm)' }}>
                          <span style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>
                            {line.speaker === 'agent' ? 'נציג' : line.speaker === 'customer' ? 'לקוח' : (line.speaker ?? '')}:
                          </span>{' '}{line.txt}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            </Card>

            {details.has_recording && (
              <button type="button" className="btn" onClick={() => { void openRecording(details); setDetails(null); }}>
                <Icon name="play" size={14} color="var(--accent)" /> האזן להקלטה
              </button>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}

function formatDateTime(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' });
}

function fmtDur(s: number): string {
  if (!s) return '—';
  const m = Math.floor(s / 60);
  const r = (s % 60).toString().padStart(2, '0');
  return `${m}:${r}`;
}
